/**
 * OAuth 2.1 consent screen.
 *
 * Supabase's OAuth server (Authentication > OAuth Server) is configured with
 * Authorization Path `/oauth/consent`. When a third-party app (Dust, Claude,
 * Cursor, ...) starts the authorize flow, Supabase validates the request and
 * redirects the signed-in user here with an `authorization_id`. This page reads
 * the pending request, shows who is asking, and approves or denies it, then
 * sends the user back to the app. Supabase mints the authorization code; this
 * is purely the consent UI.
 *
 * Security: the requesting client's name and logo come from Dynamic Client
 * Registration, i.e. they are attacker-controlled. The name is rendered as
 * plain text (React escapes it) and framed as a self-identification, and the
 * client logo is never loaded, so a malicious client cannot impersonate holito
 * branding. The route is wrapped in ProtectedRoute, so an unauthenticated user
 * is bounced to login and returned here with the same authorization_id.
 *
 * @module components/auth/OAuthConsentPage
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { hardRedirect } from '../../lib/hard-redirect';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import BrandedLineLoader from '../BrandedLineLoader';
import { ButtonContent } from '../ui/ButtonContent';

/** UI phase for the consent flow. */
type Phase = 'loading' | 'consent' | 'submitting' | 'error';

/** The subset of the authorization request shown to the user. */
interface ConsentInfo {
  clientName: string;
  userEmail: string;
}

/**
 * Consent screen for the holito OAuth 2.1 server. Renders nothing of its own
 * chrome: a single centered card on the app background.
 */
export const OAuthConsentPage: React.FC = () => {
  const { supabase } = useSupabase();
  const [searchParams] = useSearchParams();
  const authorizationId = searchParams.get('authorization_id');

  const [phase, setPhase] = useState<Phase>('loading');
  const [info, setInfo] = useState<ConsentInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [decision, setDecision] = useState<'approve' | 'deny' | null>(null);

  useEffect(() => {
    let active = true;

    async function load(): Promise<void> {
      if (!authorizationId) {
        if (active) {
          setErrorMessage('This authorization request is missing or invalid.');
          setPhase('error');
        }
        return;
      }

      const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(authorizationId);
      if (!active) return;

      if (error || !data) {
        setErrorMessage('This authorization request is invalid or has expired.');
        setPhase('error');
        return;
      }

      // Already consented: Supabase returns a ready redirect URL with the code.
      if (!('authorization_id' in data)) {
        hardRedirect(data.redirect_url);
        return;
      }

      setInfo({ clientName: data.client.name, userEmail: data.user.email });
      setPhase('consent');
    }

    void load();
    return () => {
      active = false;
    };
  }, [authorizationId, supabase]);

  const decide = useCallback(
    async (approve: boolean): Promise<void> => {
      if (!authorizationId || phase === 'submitting') return;
      setDecision(approve ? 'approve' : 'deny');
      setErrorMessage('');
      setPhase('submitting');
      try {
        const { data, error } = approve
          ? await supabase.auth.oauth.approveAuthorization(authorizationId, { skipBrowserRedirect: true })
          : await supabase.auth.oauth.denyAuthorization(authorizationId, { skipBrowserRedirect: true });

        if (error || !data) {
          setErrorMessage('Could not complete the request. Please try again.');
          setPhase('error');
          return;
        }
        hardRedirect(data.redirect_url);
      } catch (err) {
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'OAuthConsentPage.decide',
          { approve },
        );
        setErrorMessage('Could not complete the request. Please try again.');
        setPhase('error');
      }
    },
    [authorizationId, supabase, phase],
  );

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <BrandedLineLoader logoUrl={null} />
      </div>
    );
  }

  if (phase === 'error' && !info) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <AnimatedPresence show className="max-w-sm w-full text-center">
          <div className="space-y-3">
            <h1 className="text-lg font-semibold text-foreground">Authorization failed</h1>
            <p role="alert" className="text-sm text-muted-foreground">{errorMessage}</p>
          </div>
        </AnimatedPresence>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <AnimatedPresence show className="max-w-sm w-full">
        <div aria-busy={phase === 'submitting'} className="bg-surface border border-border rounded-2xl p-6 space-y-5">
          <h1 className="text-lg font-semibold text-foreground">Authorize access</h1>
          <p className="text-sm text-muted-foreground">
            An application identifying itself as{' '}
            <span className="text-foreground font-medium">{info?.clientName || 'an app'}</span> wants to
            connect to your holito account.
          </p>
          <div className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
            <span className="text-muted-foreground">Signed in as </span>
            <span className="text-foreground font-medium break-all">{info?.userEmail}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">Once connected, it can use the holito API to:</p>
            <ul className="space-y-1 text-subtle-foreground list-disc list-inside">
              <li>Read your conversations and campaigns</li>
              <li>Create campaigns and share links</li>
              <li>Delete campaigns</li>
            </ul>
          </div>
          <div>
            <AnimatedPresence show={phase === 'error'}>
              <p role="alert" className="pb-5 text-sm text-danger"><AnimatedLabel multiline text={errorMessage} /></p>
            </AnimatedPresence>
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => void decide(false)}
                disabled={phase === 'submitting'}
                aria-busy={phase === 'submitting' && decision === 'deny'}
                className="flex-1 py-2.5 rounded-lg bg-surface-hover text-foreground text-sm hover:opacity-90 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ButtonContent loading={phase === 'submitting' && decision === 'deny'}>Deny</ButtonContent>
              </button>
              <button
                type="button"
                onClick={() => void decide(true)}
                disabled={phase === 'submitting'}
                aria-busy={phase === 'submitting' && decision === 'approve'}
                className="flex-1 py-2.5 rounded-lg button-accent text-sm transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ButtonContent loading={phase === 'submitting' && decision === 'approve'}>Authorize</ButtonContent>
              </button>
            </div>
          </div>
        </div>
      </AnimatedPresence>
    </div>
  );
};

export default OAuthConsentPage;
